import { Button, DialogActions, DialogContent, FormControl, FormControlLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import AutoComplete from "src/components/shared/AutoComplete";
import useAppGlobal from "src/context/useAppGlobal";
import useDebounce from "src/hooks/useDebounce";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import useProdGroupUpdate from "src/hooks/product-groups/useProdGroupUpdate";

export default function SetUpVATDialog() {
  const { closeDialog } = useAppGlobal();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [searchProdGroup, setSearchProdGroup] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const debouncedSearchProdGroup = useDebounce(searchProdGroup, 500);
  const [value, setValue] = useState({
    overrideVat: true,
  });

  const {
    items: prodGroupItems,
    isLoading: prodGroupLoading,
    loadingMore: prodGroupLoadingMore,
    hasMore: prodGroupHasMore,
    onLoadMore: onLoadMoreProdGroup,
  } = useInfiniteAutocomplete(
    "/api/v1/product-groups",
    {
      limit: 10,
      searchField: "name",
      searchValue: debouncedSearchProdGroup || undefined,
      sortField: "name",
      sortOrder: "asc",
    },
    true,
    debouncedSearchProdGroup,
  );

  const { trigger: updateVat } = useProdGroupUpdate(value?.prodGroupId);

  const prodGroupOptions = useMemo(() => {
    return prodGroupItems.map((v) => ({
      value: v?._id,
      label: `${v?.name} (VAT hiện tại: ${v?.vat ?? 0}%)`,
      vat: v?.vat,
    }));
  }, [prodGroupItems]);

  const selectedProdGroup = useMemo(() => {
    if (!value?.prodGroupId) return null;
    return prodGroupOptions.find((item) => item.value === value.prodGroupId) || null;
  }, [prodGroupOptions, value?.prodGroupId]);

  const resetState = () => {
    setValue({ overrideVat: true });
    setSearchProdGroup("");
    setAutocompleteKey((prev) => prev + 1);
  };

  const handleClose = () => {
    resetState();
    closeDialog();
  };

  const handleInputChangeProdGroup = (_, inputValue, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchProdGroup("");
      return;
    }
    setSearchProdGroup(inputValue);
  };

  const handleUpdateVAT = async () => {
    const res = await updateVat({
      body: {
        overrideVat: value?.overrideVat === "true" ? true : false,
        vat: Number(value?.prodGroupValue),
      },
    });
    if (res?.success) {
      handleClose();
      closeSnackbar();
      return enqueueSnackbar("Cập nhật thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  return (
    <>
      <DialogContent>
        <Stack gap={2}>
          <Stack gap={1}>
            <Stack
              direction="row"
              gap={2}
              pt={2}
            >
              <AutoComplete
                key={`prod-group-${autocompleteKey}`}
                fullWidth
                options={prodGroupOptions}
                value={selectedProdGroup}
                onChange={(_, selected) => {
                  setValue((prev) => ({
                    ...prev,
                    prodGroupId: selected?.value,
                    prodGroupValue: selected?.vat ?? 0,
                  }));
                  setSearchProdGroup("");
                }}
                getOptionLabel={(option) => option?.label ?? ""}
                isOptionEqualToValue={(option, val) => option?.value === val?.value}
                loading={prodGroupLoading}
                hasMore={prodGroupHasMore}
                loadingMore={prodGroupLoadingMore}
                onLoadMore={onLoadMoreProdGroup}
                filterOptions={(opts) => opts}
                onInputChange={handleInputChangeProdGroup}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nhóm sản phẩm"
                  />
                )}
              />
              <TextField
                disabled={!value?.prodGroupId}
                label="VAT (%)"
                placeholder="Thiết lập VAT"
                fullWidth
                value={value?.prodGroupValue ?? ""}
                onChange={(e) => setValue((prev) => ({ ...prev, prodGroupValue: e.target.value }))}
                type="number"
                inputProps={{
                  min: 0,
                  max: 10,
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Stack>
          </Stack>
          <Stack>
            <Typography
              variant="subtitle1"
              gutterBottom
            >
              Vui lòng chọn:
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                // disabled={value?.prodGroupValue === undefined || value?.prodGroupValue === ""}
                value={value?.overrideVat}
                onChange={(e) => setValue((prev) => ({ ...prev, overrideVat: e.target.value }))}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Thiết lập cho tất cả các SP"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Chỉ thiết lập VAT mới cho các sản phẩm chưa tuỳ chỉnh VAT riêng"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: "0 24px 24px" }}>
        <Button
          fullWidth
          onClick={handleUpdateVAT}
          disabled={value?.overrideVat === undefined || value?.prodGroupValue === undefined || value?.prodGroupValue === ""}
          variant="contained"
        >
          Xác nhận
        </Button>
      </DialogActions>
    </>
  );
}
